import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, CircleDot, FlaskConical, MessageSquareText, PackageCheck, Search, Sparkles, Users } from "lucide-react";
import { confirmForCapture, createForCapture, createForFeedback, createForRevision, getForPilot, updateForJob } from "../lib/forPilotApi";

const STAGES = [
  ["INTAKE", "Prospect + context", "ready"],
  ["RESEARCH", "Facts separated from hypotheses", "ready"],
  ["DISCOVERY", "Capture current workflow + pain points", "next"],
  ["PILOT", "Run one real product end to end", "next"],
  ["FINDINGS", "Convert evidence into product changes", "next"],
];

const DISCOVERY_CATEGORIES = ["Pain Point", "Want", "Need", "Current Process", "Constraint", "Priority", "Objection", "Decision", "Opportunity", "Unknown", "Next Action"];
const CAPTURE_STORAGE_KEY = "lincwerks.for.daryl-pilot.captures.v1";
const PILOT_STORAGE_KEY = "lincwerks.for.daryl-pilot.production.v1";
const FEEDBACK_STORAGE_KEY = "lincwerks.for.daryl-pilot.feedback.v1";
const DEFAULT_PILOT = {
  id: "job_daryl_penlab_v1",
  revision: "V1 mechanical beta",
  status: "ready_for_fit_coupon",
  owner: "ForgeLinc / founder",
  nextAction: "Print fit coupon and validate the 9.5 mm shaft assumption before the base/cradle mule.",
};
const STATUS_OPTIONS = [
  ["ready_for_fit_coupon", "Ready for fit coupon"],
  ["prototype_queued", "Prototype queued"],
  ["printing", "Printing"],
  ["prototype_review", "Prototype review"],
  ["revision_requested", "Revision requested"],
  ["approved", "Approved"],
];

const VALIDATE = [
  "How jobs arrive today: message, email, sketch, image, CAD, or storefront",
  "How revisions and approvals are tracked across files and conversations",
  "How material, color, printer, slicer settings, due date, and quantity are handed off",
  "Where production status becomes unclear to customers or partners",
  "How prototype feedback becomes the next design revision",
  "Whether scoped partner/vendor access would reduce coordination work",
];

function loadJson(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "null");
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function loadDraftCaptures() {
  const parsed = loadJson(CAPTURE_STORAGE_KEY, []);
  if (!Array.isArray(parsed)) return [];
  return parsed.map((row) => ({
    ...row,
    provenance: row.provenance || (row.status === "confirmed" ? "customer_confirmed" : "captured_unconfirmed"),
  }));
}

export default function DarylPilotPage() {
  const [captures, setCaptures] = useState(loadDraftCaptures);
  const [category, setCategory] = useState("Pain Point");
  const [note, setNote] = useState("");
  const [pilot, setPilot] = useState(() => ({ ...DEFAULT_PILOT, ...loadJson(PILOT_STORAGE_KEY, DEFAULT_PILOT) }));
  const [feedback, setFeedback] = useState(() => loadJson(FEEDBACK_STORAGE_KEY, []));
  const [customer, setCustomer] = useState(null);
  const [research, setResearch] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [walkout, setWalkout] = useState(null);
  const [feedbackNote, setFeedbackNote] = useState("");
  const [revisionInstruction, setRevisionInstruction] = useState("");
  const [serverConnected, setServerConnected] = useState(false);
  const [syncing, setSyncing] = useState(true);
  const [savingHandoff, setSavingHandoff] = useState(false);
  const [savingRevision, setSavingRevision] = useState(false);
  const [syncError, setSyncError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getForPilot();
        if (!active) return;
        setCaptures(Array.isArray(data?.captures) ? data.captures : []);
        setFeedback(Array.isArray(data?.feedback) ? data.feedback : []);
        setCustomer(data?.customer || null);
        setResearch(Array.isArray(data?.research) ? data.research : []);
        setRevisions(Array.isArray(data?.revisions) ? data.revisions : []);
        setWalkout(data?.walkout || null);
        if (data?.jobs?.[0]) setPilot((current) => ({ ...current, ...data.jobs[0] }));
        setServerConnected(true);
        setSyncError("");
      } catch (error) {
        if (!active) return;
        setServerConnected(false);
        setSyncError("Durable pilot API is unavailable in this session. Local fallback remains active; no server write will be claimed.");
      } finally {
        if (active) setSyncing(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (serverConnected) return;
    try { window.localStorage.setItem(CAPTURE_STORAGE_KEY, JSON.stringify(captures)); } catch { /* local fallback only */ }
  }, [captures, serverConnected]);
  useEffect(() => {
    if (serverConnected) return;
    try { window.localStorage.setItem(PILOT_STORAGE_KEY, JSON.stringify(pilot)); } catch { /* local fallback only */ }
  }, [pilot, serverConnected]);
  useEffect(() => {
    if (serverConnected) return;
    try { window.localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(feedback)); } catch { /* local fallback only */ }
  }, [feedback, serverConnected]);

  const confirmedCaptures = useMemo(() => captures.filter((row) => row.provenance === "customer_confirmed"), [captures]);
  const confirmedCount = confirmedCaptures.length;
  const confirmedByCategory = useMemo(() => confirmedCaptures.reduce((groups, row) => {
    if (!groups[row.category]) groups[row.category] = [];
    groups[row.category].push(row.note || row.text);
    return groups;
  }, {}), [confirmedCaptures]);
  const hypotheses = walkout?.hypotheses || captures.filter((row) => row.provenance === "hypothesis");
  const unknowns = walkout?.unknowns || captures.filter((row) => row.provenance === "unknown" || row.category?.toLowerCase() === "unknown");
  const hasSampleData = customer?.provenance === "demo_sample" || pilot.provenance === "demo_sample";
  const statusLabel = STATUS_OPTIONS.find(([value]) => value === pilot.status)?.[1] || pilot.status;

  const addCapture = async (event) => {
    event.preventDefault();
    if (!note.trim()) return;
    const text = note.trim();
    if (serverConnected) {
      try {
        const result = await createForCapture(category, text);
        setCaptures(result?.pilot?.captures || captures);
        setNote("");
        setSyncError("");
        return;
      } catch {
        setSyncError("Capture could not be written to the durable pilot record. Nothing was promoted to customer-confirmed state.");
        return;
      }
    }
    setCaptures((current) => [...current, {
      id: Date.now(), category, note: text, text, status: "captured", provenance: "captured_unconfirmed",
      capturedAt: new Date().toISOString(), confirmedAt: null, sourceRefs: [],
    }]);
    setNote("");
  };

  const confirm = async (id) => {
    if (serverConnected) {
      try {
        const result = await confirmForCapture(id);
        setCaptures(result?.pilot?.captures || captures);
        setSyncError("");
      } catch {
        setSyncError("Confirmation was not saved. The item remains unconfirmed until the server accepts the explicit confirmation action.");
      }
      return;
    }
    setCaptures((current) => current.map((row) => row.id === id ? {
      ...row, status: "confirmed", provenance: "customer_confirmed", confirmedAt: new Date().toISOString(),
    } : row));
  };

  const removeCapture = (id) => {
    if (serverConnected) return;
    setCaptures((current) => current.filter((row) => row.id !== id));
  };

  const saveHandoff = async () => {
    if (!serverConnected || !pilot.id) return;
    setSavingHandoff(true);
    try {
      const result = await updateForJob(pilot.id, {
        status: pilot.status,
        owner: pilot.owner,
        nextAction: pilot.nextAction,
      });
      if (result?.job) setPilot((current) => ({ ...current, ...result.job }));
      setSyncError("");
    } catch {
      setSyncError("Production handoff changes were not saved to the durable pilot record.");
    } finally {
      setSavingHandoff(false);
    }
  };

  const addRevision = async (event) => {
    event.preventDefault();
    if (!serverConnected || !pilot.id || !revisionInstruction.trim()) return;
    setSavingRevision(true);
    try {
      const result = await createForRevision(pilot.id, { instruction: revisionInstruction.trim() });
      setRevisions(result?.pilot?.revisions || revisions);
      if (result?.pilot?.jobs?.[0]) setPilot((current) => ({ ...current, ...result.pilot.jobs[0] }));
      setWalkout(result?.pilot?.walkout || walkout);
      setRevisionInstruction("");
      setSyncError("");
    } catch {
      setSyncError("The new revision was not appended to the durable pilot record.");
    } finally {
      setSavingRevision(false);
    }
  };

  const addFeedback = async (event) => {
    event.preventDefault();
    if (!feedbackNote.trim()) return;
    const text = feedbackNote.trim();
    if (serverConnected && pilot.id) {
      try {
        const result = await createForFeedback(pilot.id, { note: text, result: "revise", photoRefs: [] });
        setFeedback(result?.pilot?.feedback || feedback);
        setFeedbackNote("");
        setSyncError("");
        return;
      } catch {
        setSyncError("Prototype feedback could not be saved to the durable pilot record.");
        return;
      }
    }
    setFeedback((current) => [...current, { id: Date.now(), note: text, capturedAt: new Date().toISOString(), provenance: "captured_unconfirmed" }]);
    setFeedbackNote("");
  };

  const promoteFeedback = (id) => {
    if (serverConnected) return;
    setFeedback((current) => current.map((row) => row.id === id ? { ...row, provenance: "customer_confirmed" } : row));
  };
  const removeFeedback = (id) => {
    if (serverConnected) return;
    setFeedback((current) => current.filter((row) => row.id !== id));
  };

  return (
    <div className="bw-page" data-testid="for-daryl-pilot">
      <header className="bw-hero">
        <div>
          <small>FOR · LINCFORGE CUSTOMER PROCESS PILOT</small>
          <h1>Daryl Osterloh · Monday Pilot</h1>
          <p>One workspace for intake → research → discovery → real product pilot → findings.</p>
        </div>
        <Link to="/linkforge" className="btn">Back to ForgeLinc</Link>
      </header>

      <section className="panel" style={{ marginBottom: 14, borderColor: serverConnected && !hasSampleData ? "var(--green, #22c55e)" : "var(--amber, #f59e0b)" }} data-testid="for-demo-provenance-banner">
        <div className="panel-body" style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div><b>{syncing ? "CONNECTING PILOT RECORD" : hasSampleData ? "DEMO / SAMPLE DATA" : serverConnected ? "DURABLE PILOT RECORD" : "LOCAL FALLBACK / SAMPLE DATA"}</b><p className="hint" style={{ margin: "3px 0 0" }}>{serverConnected ? "Meeting captures and explicit updates persist through the FOR pilot API. Prepared customer and product context remains demo data until replaced with sourced or customer-confirmed records." : "Prepared context and local notes remain illustrative. Customer-confirmed truth is not claimed as server-persisted while the API is unavailable."}</p></div>
          <span className={`pill ${serverConnected && !hasSampleData ? "green" : "amber"}`}>{serverConnected ? hasSampleData ? "API CONNECTED · SAMPLE CONTEXT" : "SERVER PERSISTED" : "PROVENANCE ON"}</span>
        </div>
        {syncError ? <div className="panel-body" style={{ paddingTop: 0 }}><p className="hint" style={{ margin: 0 }}>{syncError}</p></div> : null}
      </section>

      <section className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-head"><div><h2>Customer journey</h2><p>Keep facts, hypotheses, and customer-confirmed findings distinct.</p></div></div>
        <div className="panel-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
          {STAGES.map(([name, detail, state]) => <div className="wide-row" key={name} style={{ alignItems: "flex-start" }}><div><small className="hint">{state === "ready" ? "READY" : "NEXT"}</small><b style={{ display: "block" }}>{name}</b><p className="hint" style={{ fontSize: 11 }}>{detail}</p></div>{state === "ready" ? <CheckCircle2 size={16} /> : <CircleDot size={16} />}</div>)}
        </div>
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14 }}>
        <section className="panel">
          <div className="panel-head"><div><h2><Search size={14} /> What we know</h2><p>Context supplied for the pilot; public research still needs source-by-source validation before presentation.</p></div></div>
          <div className="panel-body">
            <div className="wide-row"><div><b>{customer?.displayName || "Daryl Osterloh"}</b><p className="hint">Named pilot/customer-process design partner</p></div><span className={`pill ${customer?.provenance === "public_sourced" ? "blue" : customer?.provenance === "customer_confirmed" ? "green" : "amber"}`}>{customer?.provenance === "public_sourced" ? "PUBLIC SOURCED" : customer?.provenance === "customer_confirmed" ? "CUSTOMER CONFIRMED" : "DEMO SAMPLE"}</span></div>
            {research.map((item) => <div className="wide-row" key={item.id || item.text}><div><b>{item.title || item.text}</b>{item.sourceRefs?.length ? <p className="hint">{item.sourceRefs.length} source reference{item.sourceRefs.length === 1 ? "" : "s"}</p> : null}</div><span className={`pill ${item.provenance === "public_sourced" ? "blue" : "amber"}`}>{(item.provenance || "demo_sample").replaceAll("_", " ")}</span></div>)}
            <div className="wide-row"><div><b>3D/additive manufacturing workflow</b><p className="hint">Discovery focus: design → revision → print → QA → handoff</p></div><span className="pill blue">SCOPE</span></div>
            <div className="wide-row"><div><b>Monday objective</b><p className="hint">Walk through a coherent customer journey and one real manufacturing workflow</p></div><span className="pill amber">TARGET</span></div>
          </div>
        </section>
        <section className="panel">
          <div className="panel-head"><div><h2><FlaskConical size={14} /> What we need to validate</h2><p>These are hypotheses/unknowns. Do not convert them into facts until Daryl confirms them.</p></div></div>
          <div className="panel-body">
            {hypotheses.map((item) => <div className="wide-row" key={item.id || item.text}><span>{item.note || item.text}</span><span className="pill purple">HYPOTHESIS</span></div>)}
            {unknowns.map((item) => <div className="wide-row" key={item.id || item.text}><span>{item.note || item.text}</span><span className="pill dim">UNKNOWN</span></div>)}
            {!hypotheses.length && !unknowns.length ? VALIDATE.map((item) => <div className="wide-row" key={item}><span>{item}</span><span className="pill dim">VALIDATE</span></div>) : null}
          </div>
        </section>
      </div>

      <section className="panel" style={{ marginTop: 14 }}>
        <div className="panel-head"><div><h2><PackageCheck size={14} /> Real product pilot</h2><p>PEN handoff gives FOR a concrete product instead of a fictional demo.</p></div><span className="pill purple">PEN → FOR</span></div>
        <div className="panel-body">
          <div className="wide-row"><div><b>Lacrosse figurine pen holder · V1</b><p className="hint">Standing adult/youth concept · open-hand stick-pen cradle · base tip divot · swappable nameplate/logo zones</p></div><span className="pill cyan">SPEC READY</span></div>
          <div className="wide-row"><div><b>Functional mechanical beta</b><p className="hint">130×90×16 mm base · 9.5 mm shaft assumption · 72° pen angle · open cradle · keyed figurine mounts.</p></div><span className="pill green">CAD READY</span></div>
          <div className="wide-row"><div><b>Prototype sequence</b><p className="hint">fit coupon → base/cradle mule → stability → clearance → nameplate/badge fit → organic figure handoff</p></div><span className="pill blue">PRINT NEXT</span></div>
          <div className="wide-row"><div><b>Guardrail</b><p className="hint">Mechanical CAD exists; do not claim a production-ready organic figurine mesh/STL until generated and validated.</p></div><span className="pill amber">TRUTH</span></div>
        </div>
      </section>

      <section className="panel" style={{ marginTop: 14 }} data-testid="for-production-handoff">
        <div className="panel-head"><div><h2><PackageCheck size={14} /> Revision + production handoff</h2><p>{serverConnected ? "Status and ownership save explicitly; new revisions append without overwriting prior evidence." : "Local fallback only until the durable pilot API reconnects."}</p></div><span className={`pill ${serverConnected ? "green" : "amber"}`}>{serverConnected ? "API CONNECTED" : "LOCAL FALLBACK"}</span></div>
        <div className="panel-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
          <label><small>Current revision</small><input value={pilot.revision || "No revision recorded"} readOnly /></label>
          <label><small>Production status</small><select value={pilot.status || "ready_for_fit_coupon"} onChange={(e) => setPilot((current) => ({ ...current, status: e.target.value }))}>{STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label><small>Next-action owner</small><input value={pilot.owner || ""} onChange={(e) => setPilot((current) => ({ ...current, owner: e.target.value }))} /></label>
          <label style={{ gridColumn: "1 / -1" }}><small>Concrete next action</small><textarea rows={2} value={pilot.nextAction || ""} onChange={(e) => setPilot((current) => ({ ...current, nextAction: e.target.value }))} /></label>
          <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}><button className="btn primary" onClick={saveHandoff} disabled={!serverConnected || savingHandoff}>{savingHandoff ? "Saving…" : "Save handoff"}</button></div>
          <div className="wide-row" style={{ gridColumn: "1 / -1" }}><div><small className="hint">Current handoff</small><b style={{ display: "block" }}>{pilot.revision} · {statusLabel}</b><p className="hint" style={{ margin: "3px 0 0" }}>{pilot.nextAction}</p></div><span className="pill blue">{pilot.owner || "OWNER NEEDED"}</span></div>
          <div style={{ gridColumn: "1 / -1" }}>
            <small className="hint">REVISION HISTORY</small>
            {revisions.length ? revisions.map((revision) => <div className="wide-row" key={revision.id}><div><b>V{revision.number} · {revision.status}</b><p className="hint" style={{ margin: "3px 0 0" }}>{revision.instruction}</p></div><span className="pill dim">APPENDED</span></div>) : <p className="hint">No appended revisions yet. The mechanical beta remains prepared sample context.</p>}
          </div>
          <form onSubmit={addRevision} style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}><input value={revisionInstruction} onChange={(event) => setRevisionInstruction(event.target.value)} placeholder="Instruction for the next revision…" /><button className="btn" disabled={!serverConnected || !revisionInstruction.trim() || savingRevision}>{savingRevision ? "Appending…" : "Append revision"}</button></form>
        </div>
      </section>

      <section className="panel" style={{ marginTop: 14 }} data-testid="for-prototype-feedback">
        <div className="panel-head"><div><h2><MessageSquareText size={14} /> Prototype feedback</h2><p>Capture observations without silently turning them into customer-confirmed requirements.</p></div><span className="pill blue">{feedback.length} notes</span></div>
        <div className="panel-body">
          <form onSubmit={addFeedback} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 12 }}><input value={feedbackNote} onChange={(e) => setFeedbackNote(e.target.value)} placeholder="Prototype observation or requested revision…" /><button className="btn primary" disabled={!feedbackNote.trim()}>Capture feedback</button></form>
          {feedback.length === 0 ? <p className="hint">No prototype feedback captured yet.</p> : feedback.map((row) => <div className="wide-row" key={row.id}><div><b>{row.note || row.observations?.[0]}</b><small className="hint" style={{ display: "block", marginTop: 3 }}>{row.provenance === "customer_confirmed" ? "Customer confirmed" : "Captured · not yet confirmed"}</small></div><div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>{row.provenance === "customer_confirmed" ? <span className="pill green">CONFIRMED</span> : serverConnected ? <span className="pill amber">UNCONFIRMED</span> : <button className="btn small" onClick={() => promoteFeedback(row.id)}>Confirm locally</button>}{!serverConnected ? <button className="btn small" onClick={() => removeFeedback(row.id)}>Remove</button> : null}</div></div>)}
        </div>
      </section>

      <section className="panel" style={{ marginTop: 14 }}>
        <div className="panel-head"><div><h2><MessageSquareText size={14} /> Live discovery capture</h2><p>New entries start as captured/unconfirmed and become customer-confirmed only by explicit action.</p></div><span className="pill blue">{confirmedCount} confirmed</span></div>
        <div className="panel-body">
          <form onSubmit={addCapture} style={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: 8, marginBottom: 12 }}><select value={category} onChange={(event) => setCategory(event.target.value)}>{DISCOVERY_CATEGORIES.map((value) => <option key={value}>{value}</option>)}</select><input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Capture exact customer language or a concise observation…" /><button className="btn primary" disabled={!note.trim()}>Capture</button></form>
          {captures.length === 0 ? <p className="hint">No meeting captures yet. {serverConnected ? "New captures will be written to the durable pilot record." : "Local fallback captures stay on this device until the API is available."}</p> : captures.map((row) => <div className="wide-row" key={row.id}><div><small className="hint">{row.category}</small><b style={{ display: "block" }}>{row.note || row.text}</b><small className="hint" style={{ display: "block", marginTop: 3 }}>{row.provenance === "customer_confirmed" ? "Customer confirmed" : "Captured · not yet confirmed"}</small></div><div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>{row.provenance === "customer_confirmed" ? <span className="pill green">CONFIRMED</span> : <><span className="pill amber">UNCONFIRMED</span><button className="btn small" onClick={() => confirm(row.id)}>Confirm with Daryl</button></>}{!serverConnected ? <button className="btn small" onClick={() => removeCapture(row.id)} aria-label={`Remove ${row.category} capture`}>Remove</button> : null}</div></div>)}
        </div>
      </section>

      <section className="panel" style={{ marginTop: 14 }} data-testid="for-walkout-prospectus">
        <div className="panel-head"><div><h2><Sparkles size={14} /> Preliminary walk-out prospectus</h2><p>Built only from customer-confirmed captures; unconfirmed notes stay out.</p></div><span className={`pill ${confirmedCount ? "green" : "dim"}`}>{confirmedCount ? "CUSTOMER CONFIRMED" : "WAITING"}</span></div>
        <div className="panel-body">{confirmedCount === 0 ? <p className="hint">Confirm discovery captures during the meeting and they will roll up here automatically.</p> : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10 }}>{Object.entries(confirmedByCategory).map(([group, items]) => <div className="wide-row" key={group} style={{ alignItems: "flex-start" }}><div><small className="hint">{group}</small>{items.map((item) => <p key={item} style={{ margin: "5px 0 0" }}>• {item}</p>)}</div><span className="pill green">CUSTOMER CONFIRMED</span></div>)}</div>}</div>
      </section>

      <section className="panel" style={{ marginTop: 14 }} data-testid="for-walkout-next-actions">
        <div className="panel-head"><div><h2><Sparkles size={14} /> Walk-out result + ownership</h2><p>Leave with the evidence, production state, and a concrete owned next action visible in one place.</p></div><span className="pill blue">OWNER · {pilot.owner || "UNASSIGNED"}</span></div>
        <div className="panel-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
          <div className="wide-row"><Users size={16} /><span><b>Confirmed workflow</b><small className="hint" style={{ display: "block" }}>{confirmedCount} customer-confirmed discovery item{confirmedCount === 1 ? "" : "s"}</small></span></div>
          <div className="wide-row"><Sparkles size={16} /><span><b>Problem/value map</b><small className="hint" style={{ display: "block" }}>evidence stays separate from hypotheses</small></span></div>
          <div className="wide-row"><PackageCheck size={16} /><span><b>{pilot.revision}</b><small className="hint" style={{ display: "block" }}>{statusLabel}</small></span></div>
          <div className="wide-row" style={{ gridColumn: "1 / -1" }}><div><small className="hint">NEXT ACTION</small><b style={{ display: "block" }}>{pilot.nextAction || "Assign the next action before ending the meeting."}</b><p className="hint" style={{ margin: "3px 0 0" }}>Owner: {pilot.owner || "unassigned"}</p></div><span className="pill green">WALK-OUT</span></div>
        </div>
      </section>
    </div>
  );
}
